import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import MultiComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/multi-combo-box';
import Label from 'atom-components/label';
import { useCustomerAreas } from 'services/assist-api/entities/customers-areas/customers-areas';
import { useTests } from 'services/assist-api/entities/test/tests';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';

const fieldLabel = 'Test(s)';
const fieldName = 'testIds';

function useSelectableTests(): { selectableTests: ComboBoxItem[]; isLoading: boolean } {
  // Get the current user's organisation:
  const currentOrganisation = useAuthCustomer();

  // Get the current user's areas that have marking enabled:
  const organisationAreasQuery = useCustomerAreas({
    filters: { customer: currentOrganisation?.id, 'area.markingEnabled': true }
  });

  // Get a list of tests that have marking enabled for the current organisation/marking areas:
  const testsQuery = useTests({
    filters: {
      area: organisationAreasQuery.data?.json.map((organisationArea) => organisationArea.area.id),
      markingEnabled: true
    }
  });

  const selectableTests =
    testsQuery.data?.json.map((test) => ({
      id: test.id,
      name: test.name
    })) || [];

  const isLoading = isInitialLoad(testsQuery);

  return { selectableTests, isLoading };
}

type Props = {
  triggerFormSubmission: () => void;
};

export default function TestFilter({ triggerFormSubmission }: Props): ReactElement {
  const { control, setValue } = useFormContext();
  const { selectableTests, isLoading } = useSelectableTests();

  return (
    <div>
      <Label htmlFor={fieldName}>Test(s)</Label>
      <MultiComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableTests}
        clearable
        loading={isLoading}
        onChange={(items): void => {
          const testIds = items.map((item) => item.id);
          setValue(fieldName, testIds, {
            shouldValidate: true,
            shouldTouch: true
          });
          triggerFormSubmission();
        }}
      />
    </div>
  );
}

export function TestAppliedFilter({ triggerFormSubmission }): ReactElement | null {
  const { watch, setValue } = useFormContext();
  const testIds = watch(fieldName);
  const { selectableTests } = useSelectableTests();

  if (testIds.length === 0) return null;

  return (
    <AppliedFilters.Filter name={fieldLabel}>
      {testIds.map(
        (testId: number, index: number): ReactElement => (
          <AppliedFilters.FilterItem
            key={testId}
            onClick={(): void => {
              setValue(fieldName, testIds.toSpliced(index, 1));
              triggerFormSubmission();
            }}
          >
            {selectableTests.find((test) => test.id === testId)?.name}
          </AppliedFilters.FilterItem>
        )
      )}
    </AppliedFilters.Filter>
  );
}
